"use client";

import type React from "react";
import { Children, createContext, useMemo } from "react";
import { Box } from "../Box/Box";
import type { ResponsiveSpace, Space } from "../Box/useBoxStyles";
import type { ColumnProps } from "../Column/Column";
import { type AlignY, alignYToFlexAlign } from "../utils/align";
import { negativeMarginLeft } from "../utils/negativeMarginCss";
import {
  normaliseResponsiveProp,
  type ResponsiveProp,
} from "../utils/responsiveProp";
import {
  type ResponsiveRangeProps,
  resolveResponsiveRangeProps,
} from "../utils/responsiveRangeProps";

interface ColumnsContextValue {
  collapseMobile: boolean;
  collapseTablet: boolean;
  mobileSpace: Space;
  tabletSpace: Space;
  desktopSpace: Space;
}
export const ColumnsContext = createContext<ColumnsContextValue>({
  collapseMobile: false,
  collapseTablet: false,
  mobileSpace: "none",
  tabletSpace: "none",
  desktopSpace: "none",
});

export interface ColumnsProps {
  children:
    | Array<React.ReactElement<ColumnProps> | null>
    | React.ReactElement<ColumnProps>
    | null;
  collapseBelow?: ResponsiveRangeProps["below"];
  reverse?: boolean;
  alignY?: ResponsiveProp<AlignY>;
  space: ResponsiveSpace;
}

export const Columns = ({
  children,
  collapseBelow,
  reverse = false,
  space = "none",
  alignY,
}: ColumnsProps): React.ReactNode => {
  const [mobileSpace, tabletSpace, desktopSpace] =
    normaliseResponsiveProp(space);

  const [collapseMobile, collapseTablet] = resolveResponsiveRangeProps({
    below: collapseBelow,
  });

  // Prevent re-renders when context values haven't changed
  const columnsContextValue = useMemo(
    () => ({
      collapseMobile,
      collapseTablet,
      mobileSpace,
      tabletSpace,
      desktopSpace,
    }),
    [collapseMobile, collapseTablet, mobileSpace, tabletSpace, desktopSpace],
  );

  const rowReverseTablet = collapseMobile && reverse;
  const rowReverseDesktop = (collapseMobile || collapseTablet) && reverse;
  const reverseDocumentOrder = !collapseMobile && !collapseTablet && reverse;

  const [lmClassName, lmCss] = negativeMarginLeft([
    collapseMobile ? "none" : mobileSpace,
    collapseTablet ? "none" : tabletSpace,
    desktopSpace,
  ]);

  return (
    <Box
      display={[
        collapseMobile ? "block" : "flex",
        collapseTablet ? "block" : "flex",
        "flex",
      ]}
      alignItems={alignYToFlexAlign(alignY)}
      flexDirection={[
        collapseMobile ? "column" : "row",
        // eslint-disable-next-line no-nested-ternary
        collapseTablet ? "column" : rowReverseTablet ? "rowReverse" : "row",
        rowReverseDesktop ? "rowReverse" : "row",
      ]}
      className={lmClassName}
    >
      {lmCss}
      <ColumnsContext.Provider value={columnsContextValue}>
        {reverseDocumentOrder ? Children.toArray(children).reverse() : children}
      </ColumnsContext.Provider>
    </Box>
  );
};
